/**
 * 故事板底部缩略图总览条（spec 2.4 / 6.2）—— v2 视觉
 *
 * - 一屏可看到所有分镜的缩略图，点击定位 / 高亮当前选中
 * - 支持拖拽重排（一屏可见全部 + 拖动距离短，是最理想的重排载体）
 * - 视觉与 ShotCard 保持一致的状态色（emerald / rose / sky / amber / 灰）
 * - 顶部细标签 + 总数；缩略图为 16:9 小卡 + 序号
 */

import type { DirectorShotEnriched } from '../../services/directorApi';
import { ImageOff, GripHorizontal } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

interface Props {
  shots: DirectorShotEnriched[];
  selectedShotId: string | null;
  onSelect: (shotId: string) => void;
  /** ---- 拖拽重排（可选；不传则纯展示 + 点击定位）---- */
  /** 是否允许拖拽（项目 composing/archived 时为 false） */
  draggable?: boolean;
  draggingShotId?: string | null;
  dragOverShotId?: string | null;
  onDragStartShot?: (shotId: string) => void;
  onDragEnterShot?: (shotId: string) => void;
  onDragEndShot?: () => void;
  onDropShot?: (targetShotId: string) => void;
}

export function ShotThumbnailBar({
  shots,
  selectedShotId,
  onSelect,
  draggable = false,
  draggingShotId = null,
  dragOverShotId = null,
  onDragStartShot,
  onDragEnterShot,
  onDragEndShot,
  onDropShot,
}: Props) {
  if (shots.length === 0) return null;

  return (
    <div className="rounded-2xl border border-white/70 bg-white/85 p-3 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
          分镜总览
          {draggable && (
            <span className="ml-1 inline-flex items-center gap-1 normal-case tracking-normal text-[10px] font-normal text-slate-400">
              <GripHorizontal className="h-3 w-3" /> 拖动可调整顺序
            </span>
          )}
        </h4>
        <span className="text-[11px] text-slate-400">{shots.length} 个</span>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-color:rgb(203_213_225)_transparent] [scrollbar-width:thin]">
        {shots.map((shot, idx) => {
          const isSelected = shot.id === selectedShotId;
          const status = shot.activeVideoJob?.status ?? 'none';
          // 兼容三种分镜模式的"是否有内容 / 用哪张图作封面"判断（与 ShotCard 保持一致）。
          const isFrameSequence = shot.mode === 'frame_sequence';
          const isTrinityMode = shot.mode === 'trinity_cards';
          const hasContent = isFrameSequence
            ? (shot.frameCount ?? 0) > 0
            : isTrinityMode
              ? (shot.trinityCardCount ?? 0) > 0
              : !!shot.lockedKeyframeImageJobId;
          const coverUrl = isFrameSequence
            ? shot.firstFrameUrl ?? null
            : isTrinityMode
              ? shot.trinityCoverUrl ?? null
              : shot.lockedKeyframeUrl ?? null;
          const isDragging = draggingShotId === shot.id;
          const isDragOver = dragOverShotId === shot.id && draggingShotId !== shot.id;

          // 状态边框 + 角标颜色
          const borderClass = isSelected
            ? isTrinityMode
              ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-white shadow-[0_8px_22px_rgba(139,92,246,0.35)] -translate-y-0.5'
              : 'ring-2 ring-sky-500 ring-offset-2 ring-offset-white shadow-[0_8px_22px_rgba(56,189,248,0.35)] -translate-y-0.5'
            : !hasContent
              ? 'ring-1 ring-dashed ring-slate-300/80 hover:ring-slate-400'
              : status === 'completed'
                ? 'ring-1 ring-emerald-200 hover:ring-emerald-300'
                : status === 'failed' || status === 'timeout'
                  ? 'ring-1 ring-rose-200 hover:ring-rose-300'
                    : status === 'pending' || status === 'submitting' || status === 'processing'
                      ? 'ring-1 ring-amber-200 hover:ring-amber-300'
                      : isTrinityMode
                        ? 'ring-1 ring-violet-200 hover:ring-violet-300'
                        : 'ring-1 ring-sky-200 hover:ring-sky-300';

          // 状态点
          const dotClass =
            !hasContent
              ? 'bg-slate-300'
              : status === 'completed'
                ? 'bg-emerald-400'
                : status === 'failed' || status === 'timeout'
                  ? 'bg-rose-400'
                  : status === 'queued'
                    ? 'bg-sky-400'
                    : status === 'pending' || status === 'submitting' || status === 'processing'
                      ? 'bg-amber-400 animate-pulse'
                      : isTrinityMode
                        ? 'bg-violet-400'
                        : 'bg-sky-400';

          return (
            <button
              key={shot.id}
              type="button"
              draggable={draggable}
              onClick={() => onSelect(shot.id)}
              onDragStart={(e) => {
                if (!draggable) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.effectAllowed = 'move';
                try {
                  e.dataTransfer.setData('text/plain', shot.id);
                } catch {
                  // 忽略
                }
                onDragStartShot?.(shot.id);
              }}
              onDragEnter={(e) => {
                if (!draggable) return;
                e.preventDefault();
                onDragEnterShot?.(shot.id);
              }}
              onDragOver={(e) => {
                if (draggable) e.preventDefault();
              }}
              onDrop={(e) => {
                if (!draggable) return;
                e.preventDefault();
                onDropShot?.(shot.id);
              }}
              onDragEnd={() => onDragEndShot?.()}
              className={`group relative h-12 w-[72px] shrink-0 overflow-hidden rounded-xl bg-slate-100 transition-all ${borderClass} ${
                draggable ? 'cursor-grab active:cursor-grabbing' : ''
              } ${isDragging ? 'opacity-40' : ''} ${
                isDragOver ? 'ring-2 ring-sky-400 ring-offset-1 ring-offset-white' : ''
              }`}
              title={shot.title || `分镜 #${idx + 1}`}
            >
              {coverUrl ? (
                <img
                  src={resolveBrowserMediaUrl(coverUrl)}
                  alt=""
                  draggable={false}
                  className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-110"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-slate-400">
                  <ImageOff className="h-3 w-3" />
                  <span className="text-[9px]">#{idx + 1}</span>
                </div>
              )}
              {/* 序号 */}
              <span className="absolute left-1 top-1 inline-flex items-center gap-1 rounded-md bg-black/60 px-1 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
                <span className={`h-1 w-1 rounded-full ${dotClass}`} />
                {String(idx + 1).padStart(2, '0')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
